import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ChevronRight, RefreshCw } from 'lucide-react';

/**
 * The shell every dashboard card shares: a white panel with a header (icon, title, an
 * optional count and an optional action on the right) and a body.
 *
 * @param {object} props
 * @param {string} props.title
 * @param {React.ComponentType} [props.icon]   Lucide icon shown in the header.
 * @param {number} [props.count]               Shown as a pill after the title.
 * @param {React.ReactNode} [props.subtitle]   One muted line under the title.
 * @param {React.ReactNode} [props.action]     Right-hand header slot (a CardLink, buttons).
 * @param {string} [props.className]           Extra classes for the panel (grid spans).
 * @param {string} props['data-testid']
 */
export const DashboardCard = ({
  title, icon: Icon, count, subtitle, action, className = '', children, 'data-testid': testId,
}) => {
  const headingId = testId ? `${testId}-title` : undefined;
  return (
    <section
      data-testid={testId}
      aria-labelledby={headingId}
      className={`bg-white rounded-lg shadow-soft p-5 sm:p-6 flex flex-col min-w-0 ${className}`.trim()}
    >
      <header className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          {Icon && (
            <span className="flex-shrink-0 p-2 rounded-lg bg-primary-50">
              <Icon className="h-5 w-5 text-primary-600" aria-hidden="true" />
            </span>
          )}
          <div className="min-w-0">
            <h2 id={headingId} className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <span className="break-words">{title}</span>
              {count != null && (
                <span
                  data-testid={testId ? `${testId}-count` : undefined}
                  className="flex-shrink-0 inline-flex items-center justify-center min-w-[1.5rem] px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold"
                >
                  {count}
                </span>
              )}
            </h2>
            {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>
        {action && <div className="flex-shrink-0 flex items-center gap-1">{action}</div>}
      </header>
      <div className="flex-1 min-h-0 flex flex-col">{children}</div>
    </section>
  );
};

/** A small "View all ›" style link for a card header. */
export const CardLink = ({ to, children, ...rest }) => (
  <Link
    to={to}
    className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap"
    {...rest}
  >
    {children}
    <ChevronRight className="h-4 w-4 ml-0.5" aria-hidden="true" />
  </Link>
);

/** Placeholder rows while a card loads. */
export const CardLoading = ({ rows = 3, label }) => (
  <div role="status" aria-label={label} className="space-y-3 animate-pulse">
    {Array.from({ length: rows }, (_, index) => (
      <div key={index} className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-gray-100 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-3 bg-gray-100 rounded w-2/3" />
          <div className="h-3 bg-gray-100 rounded w-1/3" />
        </div>
      </div>
    ))}
  </div>
);

/** What a card shows when its request failed, with a way to try again. */
export const CardError = ({ message, onRetry, 'data-testid': testId }) => (
  <div
    role="alert"
    data-testid={testId}
    className="flex flex-col items-center justify-center text-center py-6 px-4 rounded-lg bg-danger-50"
  >
    <AlertCircle className="h-6 w-6 text-danger-500" aria-hidden="true" />
    <p className="mt-2 text-sm text-danger-700">{message}</p>
    {onRetry && (
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-danger-700 hover:text-danger-800"
      >
        <RefreshCw className="h-4 w-4" aria-hidden="true" />
        Try again
      </button>
    )}
  </div>
);

/** What a card shows when there is nothing to list. */
export const CardEmpty = ({ icon: Icon, children, 'data-testid': testId }) => (
  <div
    data-testid={testId}
    className="flex-1 flex flex-col items-center justify-center text-center py-6 px-4"
  >
    {Icon && <Icon className="h-8 w-8 text-gray-300" aria-hidden="true" />}
    <p className="mt-2 text-sm text-gray-500">{children}</p>
  </div>
);
